<template>
  <div class="role-management">
    <div class="page-header">
      <h2>角色管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增角色</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="角色名称">
        <el-input v-model="searchForm.role_name" placeholder="请输入角色名称" clearable />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="禁用" :value="0" />
          <el-option label="启用" :value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="roleList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="role_name" label="角色名称" />
      <el-table-column prop="role_desc" label="角色描述" />
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
            {{ scope.row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="250" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="viewRole(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="editRole(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="info-btn" @click="assignPermissions(scope.row)">分配权限</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="deleteRole(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      />
    </div>

    <!-- 角色表单对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form ref="roleForm" :model="roleForm" :rules="roleRules" label-width="100px">
        <el-form-item label="角色名称" prop="role_name">
          <el-input v-model="roleForm.role_name" />
        </el-form-item>
        <el-form-item label="角色描述" prop="role_desc">
          <el-input v-model="roleForm.role_desc" type="textarea" rows="3" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="roleForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRole">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分配权限对话框 -->
    <el-dialog title="分配权限" :visible.sync="permissionDialogVisible" width="600px">
      <el-form label-width="100px">
        <el-form-item label="角色名称">
          <span>{{ currentRole.role_name }}</span>
        </el-form-item>
        <el-form-item label="权限">
          <el-tree
            ref="permissionTree"
            :data="permissionTree"
            show-checkbox
            node-key="id"
            :props="{ label: 'permission_name', children: 'children' }"
            :default-checked-keys="selectedPermissions"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePermissions">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRoleList, createRole, updateRole, deleteRole, assignRolePermissions, getRolePermissions } from '../../api/system'

export default {
  name: 'RoleManagement',
  data() {
    return {
      loading: false,
      roleList: [],
      permissionTree: [],
      searchForm: {
        role_name: '',
        status: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增角色',
      isEdit: false,
      roleForm: {
        id: null,
        role_name: '',
        role_desc: '',
        status: 1
      },
      roleRules: {
        role_name: [
          { required: true, message: '请输入角色名称', trigger: 'blur' }
        ]
      },
      permissionDialogVisible: false,
      currentRole: {},
      selectedPermissions: []
    }
  },
  created() {
    this.fetchRoleList()
    this.fetchPermissionTree()
  },
  methods: {
    async fetchRoleList() {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.page,
          limit: this.pagination.limit
        }
        const response = await getRoleList(params)
        if (response.code === 200) {
          this.roleList = response.data.list
          this.pagination.total = response.data.total
        }
      } catch (error) {
        this.$message.error('获取角色列表失败')
      } finally {
        this.loading = false
      }
    },
    async fetchPermissionTree() {
      try {
        // 这里需要调用权限树API
        // const response = await getPermissionTree()
        // this.permissionTree = response.data
        this.permissionTree = [
          {
            id: 1,
            permission_name: '系统管理',
            children: [
              { id: 11, permission_name: '用户管理' },
              { id: 12, permission_name: '角色管理' },
              { id: 13, permission_name: '权限管理' },
              { id: 14, permission_name: '部门管理' }
            ]
          },
          {
            id: 2,
            permission_name: '商城管理',
            children: [
              { id: 21, permission_name: '店主管理' },
              { id: 22, permission_name: '店铺管理' },
              { id: 23, permission_name: '商品管理' },
              { id: 24, permission_name: '订单管理' }
            ]
          }
        ]
      } catch (error) {
        console.error('获取权限树失败', error)
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchRoleList()
    },
    handleReset() {
      this.searchForm = {
        role_name: '',
        status: ''
      }
      this.pagination.page = 1
      this.fetchRoleList()
    },
    handleSizeChange(val) {
      this.pagination.limit = val
      this.fetchRoleList()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.fetchRoleList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增角色'
      this.isEdit = false
      this.roleForm = {
        id: null,
        role_name: '',
        role_desc: '',
        status: 1
      }
      this.dialogVisible = true
    },
    viewRole(row) {
      // 查看角色详情
      this.$message.info('查看功能待实现')
    },
    editRole(row) {
      this.dialogTitle = '编辑角色'
      this.isEdit = true
      this.roleForm = { ...row }
      this.dialogVisible = true
    },
    async saveRole() {
      try {
        await this.$refs.roleForm.validate()
        let response
        if (this.isEdit) {
          response = await updateRole(this.roleForm)
        } else {
          response = await createRole(this.roleForm)
        }
        
        if (response.code === 200) {
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.fetchRoleList()
        } else {
          this.$message.error(response.msg || '操作失败')
        }
      } catch (error) {
        console.error('保存角色失败', error)
      }
    },
    async assignPermissions(row) {
      this.currentRole = row
      try {
        const response = await getRolePermissions({ role_id: row.id })
        if (response.code === 200) {
          this.selectedPermissions = response.data.map(permission => permission.id)
        }
      } catch (error) {
        console.error('获取角色权限失败', error)
      }
      this.permissionDialogVisible = true
    },
    async savePermissions() {
      try {
        const checkedKeys = this.$refs.permissionTree.getCheckedKeys()
        const halfCheckedKeys = this.$refs.permissionTree.getHalfCheckedKeys()
        const allCheckedKeys = [...checkedKeys, ...halfCheckedKeys]
        
        const response = await assignRolePermissions({
          role_id: this.currentRole.id,
          permission_ids: allCheckedKeys
        })
        
        if (response.code === 200) {
          this.$message.success('分配权限成功')
          this.permissionDialogVisible = false
        } else {
          this.$message.error(response.msg || '分配权限失败')
        }
      } catch (error) {
        this.$message.error('分配权限失败')
      }
    },
    async deleteRole(row) {
      try {
        await this.$confirm('确定要删除该角色吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await deleteRole({ id: row.id })
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.fetchRoleList()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.role-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .info-btn {
  color: #909399;
}

.table-operations .info-btn:hover {
  color: #a6a9ad;
  background-color: #f4f4f5;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}
</style>